<template>
  <div v-if="visible" class="modal d-block" tabindex="-1">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Transfer Transaction #{{ transactionId }}</h5>
          <button
            type="button"
            class="btn-close"
            @click="$emit('cancel')"
          ></button>
        </div>
        <div class="modal-body">
          <!-- Transfer in Progress -->
          <div v-if="isTransferring" class="text-center py-5">
            <div
              class="spinner-border text-primary mb-3"
              role="status"
              style="width: 3rem; height: 3rem"
            >
              <span class="visually-hidden">Transferring...</span>
            </div>
            <h5>Transfer in Progress</h5>
            <p class="text-muted">
              Transferring volumes to registry. This may take several
              minutes.<br />
              Please wait...
            </p>
            <small class="text-muted">Checking status every 5 seconds</small>
          </div>

          <!-- Loading Breakdown or Mapping -->
          <LoadingIndicatorComponent
            v-else-if="isLoadingBreakdown || isLoadingMapping"
          />

          <!-- Region Breakdown -->
          <div v-else>
            <div class="mb-3 d-flex justify-content-between align-items-center">
              <h6>Region Breakdown</h6>
              <button
                class="btn btn-sm btn-outline-primary"
                @click="checkAllRegistries"
                :disabled="isCheckingStatus"
              >
                <span
                  v-if="isCheckingStatus"
                  class="spinner-border spinner-border-sm me-1"
                ></span>
                Check All Registries
              </button>
            </div>

            <RegionBreakdownTable
              :regionBreakdown="regionBreakdown"
              @view-details="showRegionDetails"
            >
              <template #extra-headers>
                <th>Registry Status</th>
                <th>Check</th>
                <th>Ignore</th>
              </template>
              <template #extra-columns="{ region }">
                <td>
                  <span class="badge" :class="getRegistryStatusClass(region)">
                    {{ getRegistryStatusText(region) }}
                  </span>
                </td>
                <td>
                  <button
                    class="btn btn-sm btn-outline-info"
                    @click="checkRegionRegistry(region)"
                    :disabled="isCheckingRegion[region]"
                  >
                    <span
                      v-if="isCheckingRegion[region]"
                      class="spinner-border spinner-border-sm"
                    ></span>
                    <span v-else>Check Status</span>
                  </button>
                </td>
                <td>
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :value="region"
                    v-model="ignoredRegions"
                  />
                </td>
              </template>
            </RegionBreakdownTable>

            <div
              v-if="Object.keys(regionBreakdown).length === 0"
              class="alert alert-warning"
            >
              No regions found for this transaction.
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="$emit('cancel')"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="confirmTransfer"
            :disabled="isTransferring || isLoadingBreakdown || isLoadingMapping"
          >
            <span
              v-if="isTransferring"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Confirm Transfer
          </button>
        </div>
      </div>
    </div>

    <!-- Region Details Modal -->
    <BreakdownDetailsModal
      v-if="showDetailsModal"
      ref="detailsModal"
      :title="modalTitle"
      :mode="modalMode"
      :companies="modalCompanies"
      :portfolioItems="modalPortfolioItems"
      :companyPortfolioItemsMap="modalCompanyPortfolioItemsMap"
    />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useRegistryStatus } from "@/registry/useRegistryStatus";
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import BreakdownDetailsModal from "./components/BreakdownDetailsModal.vue";
import RegionBreakdownTable from "./components/RegionBreakdownTable.vue";

const POLLING_INTERVAL_MS = 5000;

const props = defineProps<{
  visible: boolean;
  transactionId: number;
}>();

const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

const { globalErrorHandler } = useGlobalHandlers();
const { integrationStatus, checkRegistryStatus } = useRegistryStatus();

const regionBreakdown = ref<Record<string, any>>({});
const regionToRegistry = ref<Record<string, string>>({});
const ignoredRegions = ref<string[]>([]);
const isLoadingBreakdown = ref(false);
const isLoadingMapping = ref(false);
const isTransferring = ref(false);
const isCheckingStatus = ref(false);
const isCheckingRegion = reactive<Record<string, boolean>>({});
const transferPollingInterval = ref<number | null>(null);

// Region details modal
const showDetailsModal = ref(false);
const detailsModal = ref<InstanceType<typeof BreakdownDetailsModal> | null>(
  null
);
const modalTitle = ref("");
const modalMode = ref<"companies" | "certificates">("companies");
const modalCompanies = ref<any[]>([]);
const modalPortfolioItems = ref<any[]>([]);
const modalCompanyPortfolioItemsMap = ref<Record<number, any[]>>({});

const fetchRegionToRegistryMapping = async () => {
  isLoadingMapping.value = true;
  try {
    const response = await goSolidApi.get(
      "/api/admin/transactions/country-registry-mapping"
    );
    if (response.status === 200 && response.data) {
      regionToRegistry.value = Object.fromEntries(
        Object.entries(
          response.data as Record<
            string,
            { integration_name: string; registration_authority: string }
          >
        ).map(([country, info]) => [country, info.integration_name])
      );
    } else {
      throw new Error("Error fetching region-registry mapping.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoadingMapping.value = false;
  }
};

const fetchRegionBreakdown = async () => {
  isLoadingBreakdown.value = true;
  try {
    const response = await goSolidApi.get(
      `/api/admin/transactions/${props.transactionId}`
    );
    if (response.status === 200 && response.data) {
      regionBreakdown.value = response.data.current_registry_breakdown || {};
    } else {
      throw new Error("Error fetching region breakdown.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoadingBreakdown.value = false;
  }
};

const checkAllRegistries = async () => {
  isCheckingStatus.value = true;
  try {
    // Check all registries in parallel
    const regions = Object.keys(regionBreakdown.value);
    await Promise.allSettled(
      regions.map((region) => checkRegionRegistry(region))
    );
  } finally {
    isCheckingStatus.value = false;
  }
};

const checkRegionRegistry = async (region: string) => {
  const registryName = regionToRegistry.value[region];
  if (!registryName) return;

  isCheckingRegion[region] = true;
  try {
    await checkRegistryStatus(registryName);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Error already handled by composable
  } finally {
    isCheckingRegion[region] = false;
  }
};

const getRegistryStatusClass = (region: string): string => {
  const registryName = regionToRegistry.value[region];
  if (!registryName || !integrationStatus.value[registryName]) {
    return "bg-secondary";
  }

  const status = integrationStatus.value[registryName];
  if (status.online === true) return "bg-success";
  if (status.online === false) return "bg-danger";
  return "bg-secondary";
};

const getRegistryStatusText = (region: string): string => {
  const registryName = regionToRegistry.value[region];
  if (!registryName || !integrationStatus.value[registryName]) {
    return "Unknown";
  }

  const status = integrationStatus.value[registryName];
  if (status.online === true) return "Online";
  if (status.online === false) return "Offline";
  return "Unknown";
};

const showRegionDetails = async (region: string) => {
  const regionData = regionBreakdown.value[region];
  if (!regionData) return;

  modalTitle.value = `${region} Details`;
  modalMode.value = "companies";
  modalCompanies.value = regionData.companies || [];
  modalPortfolioItems.value = regionData.portfolio_items || [];

  const itemMap: Record<number, any[]> = {};
  modalPortfolioItems.value.forEach((item: any) => {
    if (!itemMap[item.company_id]) {
      itemMap[item.company_id] = [];
    }
    itemMap[item.company_id].push(item);
  });
  modalCompanyPortfolioItemsMap.value = itemMap;

  showDetailsModal.value = true;
  await nextTick();
  detailsModal.value?.toggleModal();
};

const confirmTransfer = async () => {
  if (!props.transactionId) return;

  isTransferring.value = true;

  try {
    // Trigger async transfer
    const response = await goSolidApi.post(
      `/api/admin/transactions/${props.transactionId}/transfer`,
      {
        ignored_regions: ignoredRegions.value,
      }
    );

    if (response.status === 202 && response.data) {
      // Start polling for completion
      startTransferPolling();
    } else {
      throw new Error("Error starting transfer.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
    isTransferring.value = false;
  }
};

const startTransferPolling = () => {
  if (transferPollingInterval.value) {
    clearInterval(transferPollingInterval.value);
  }

  transferPollingInterval.value = window.setInterval(async () => {
    try {
      const response = await goSolidApi.get(
        `/api/admin/transactions/${props.transactionId}/transfer-status`
      );

      if (response.status === 200 && response.data) {
        if (response.data.is_complete) {
          stopTransferPolling();
          emit("confirm");
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      // Continue polling even if one check fails
    }
  }, POLLING_INTERVAL_MS);
};

const stopTransferPolling = () => {
  if (transferPollingInterval.value) {
    clearInterval(transferPollingInterval.value);
    transferPollingInterval.value = null;
  }
  isTransferring.value = false;
};

// Clean up interval on unmount
const cleanupPolling = () => {
  stopTransferPolling();
};

// Fetch breakdown and mapping when modal becomes visible
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      fetchRegionToRegistryMapping();
      fetchRegionBreakdown();
      ignoredRegions.value = [];
      stopTransferPolling(); // Clean up any existing polling
    } else {
      // Clean up when modal closes
      stopTransferPolling();
    }
  }
);

onMounted(() => {
  if (props.visible) {
    fetchRegionToRegistryMapping();
    fetchRegionBreakdown();
  }
});

// Clean up on unmount
onBeforeUnmount(() => {
  cleanupPolling();
});
</script>

<style scoped lang="scss">
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
